import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import protocolRegistrationService from '../../services/protocolRegistration.service';

const initialForm = {
  system_metadata: {
    research_type: '',
    application_status: '',
  },
  section_1_research_identification: {
    official_title: '',
    scientific_title: '',
    scientific_title_acronym: '',
    public_title: '',
    public_title_acronym: '',
    jrtc_number: '',
  },
  section_2_sponsor_and_personnel: {
    sponsor: {
      name: '',
      representative: '',
      erad_id: '',
      address: '',
      phone: '',
      email: '',
    },
    public_query_contact: {
      name: '',
      affiliation: '',
      phone: '',
      email: '',
    },
    principal_investigator: {
      name: '',
      erad_id: '',
      affiliation_institution: '',
      administrator_name: '',
      admin_permission_granted: false,
    },
  },
  section_3_study_design: {
    purpose: '',
    phase: '',
    study_period: '',
    planned_sample_size: '',
    study_type: '',
    design_features: {
      placebo_used: false,
      blinding_used: false,
      randomization_used: false,
    },
    eligibility: {
      inclusion_criteria: '',
      exclusion_criteria: '',
      age_min: '',
      age_max: '',
      gender: '',
    },
  },
  section_4_intervention_and_products: {
    target_condition: '',
    condition_code: '',
    intervention_details: '',
    product_category: '',
    product_status: '',
    generic_name: '',
    brand_name: '',
  },
  section_5_outcomes: {
    primary_outcomes: [{ name: '', measure: '' }],
    secondary_outcomes: [{ name: '', measure: '' }],
  },
  section_6_ethics_and_funding: {
    certified_review_board: {
      name: '',
      certification_number: '',
      approval_date: '',
      review_result: '',
    },
    funding_source: {
      provider_name: '',
      research_funds_provided: false,
    },
    insurance: {
      compensation_available: false,
      liability_insurance: '',
    },
  },
  section_7_transparency: {
    ipd_sharing_plan: false,
    ipd_sharing_details: '',
  },
};

const researchTypeOptions = ['Specified', 'Non-Specified', 'Regenerative Medicine'];
const genderOptions = ['Male', 'Female', 'Both'];
const productStatusOptions = ['Unapproved', 'Off-label', 'Approved'];

const JapanTrialRegistry = ({ projectId }) => {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const load = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const regData = await protocolRegistrationService.getByProject(projectId);
        const japan = regData?.japan_registry || {};
        const merged = {
          ...initialForm,
          ...japan,
          system_metadata: {
            ...initialForm.system_metadata,
            ...(japan.system_metadata || {}),
          },
          section_1_research_identification: {
            ...initialForm.section_1_research_identification,
            ...(japan.section_1_research_identification || {}),
          },
          section_2_sponsor_and_personnel: {
            sponsor: {
              ...initialForm.section_2_sponsor_and_personnel.sponsor,
              ...(japan.section_2_sponsor_and_personnel?.sponsor || {}),
            },
            public_query_contact: {
              ...initialForm.section_2_sponsor_and_personnel.public_query_contact,
              ...(japan.section_2_sponsor_and_personnel?.public_query_contact || {}),
            },
            principal_investigator: {
              ...initialForm.section_2_sponsor_and_personnel.principal_investigator,
              ...(japan.section_2_sponsor_and_personnel?.principal_investigator || {}),
            },
          },
          section_3_study_design: {
            ...initialForm.section_3_study_design,
            ...(japan.section_3_study_design || {}),
            design_features: {
              ...initialForm.section_3_study_design.design_features,
              ...(japan.section_3_study_design?.design_features || {}),
            },
            eligibility: {
              ...initialForm.section_3_study_design.eligibility,
              ...(japan.section_3_study_design?.eligibility || {}),
            },
          },
          section_4_intervention_and_products: {
            ...initialForm.section_4_intervention_and_products,
            ...(japan.section_4_intervention_and_products || {}),
          },
          section_5_outcomes: {
            ...initialForm.section_5_outcomes,
            ...(japan.section_5_outcomes || {}),
          },
          section_6_ethics_and_funding: {
            certified_review_board: {
              ...initialForm.section_6_ethics_and_funding.certified_review_board,
              ...(japan.section_6_ethics_and_funding?.certified_review_board || {}),
            },
            funding_source: {
              ...initialForm.section_6_ethics_and_funding.funding_source,
              ...(japan.section_6_ethics_and_funding?.funding_source || {}),
            },
            insurance: {
              ...initialForm.section_6_ethics_and_funding.insurance,
              ...(japan.section_6_ethics_and_funding?.insurance || {}),
            },
          },
          section_7_transparency: {
            ...initialForm.section_7_transparency,
            ...(japan.section_7_transparency || {}),
          },
        };

        // Ensure section_5_outcomes use primary_outcomes / secondary_outcomes arrays of { name, measure }
        const normOutcome = (o) =>
          o && typeof o === 'object' && ('name' in o || 'measure' in o)
            ? { name: o.name ?? '', measure: o.measure ?? '' }
            : { name: typeof o === 'string' ? o : '', measure: '' };
        if (
          !Array.isArray(merged.section_5_outcomes.primary_outcomes) ||
          merged.section_5_outcomes.primary_outcomes.length === 0
        ) {
          // Legacy: single primary_outcome string
          const legacy = merged.section_5_outcomes.primary_outcome;
          merged.section_5_outcomes.primary_outcomes =
            legacy && String(legacy).trim()
              ? [{ name: String(legacy).trim(), measure: '' }]
              : [{ name: '', measure: '' }];
        } else {
          merged.section_5_outcomes.primary_outcomes = merged.section_5_outcomes.primary_outcomes.map(
            normOutcome
          );
        }
        if (
          !Array.isArray(merged.section_5_outcomes.secondary_outcomes) ||
          merged.section_5_outcomes.secondary_outcomes.length === 0
        ) {
          // Legacy: secondary_outcomes as array of strings
          const legacy = merged.section_5_outcomes.secondary_outcomes;
          const fromStrings = Array.isArray(legacy)
            ? legacy.filter((s) => (s || '').trim()).map((s) => ({ name: String(s).trim(), measure: '' }))
            : [];
          merged.section_5_outcomes.secondary_outcomes =
            fromStrings.length > 0 ? fromStrings : [{ name: '', measure: '' }];
        } else {
          merged.section_5_outcomes.secondary_outcomes =
            merged.section_5_outcomes.secondary_outcomes.map(normOutcome);
        }

        // Convert approval_date to yyyy-mm-dd for input[type=date]
        const crb = merged.section_6_ethics_and_funding.certified_review_board;
        if (crb.approval_date) {
          const d = new Date(crb.approval_date);
          // Only use valid dates
          if (!Number.isNaN(d.getTime())) {
            merged.section_6_ethics_and_funding.certified_review_board.approval_date =
              d.toISOString().split('T')[0];
          } else {
            merged.section_6_ethics_and_funding.certified_review_board.approval_date = '';
          }
        }

        setFormData(merged);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading Japan registry:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load Japan registry data',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [projectId]);

  const handleNestedChange = (path, field, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      const segments = path.split('.');
      let current = updated;
      segments.forEach((seg, idx) => {
        if (idx === segments.length - 1) {
          current[seg] = { ...(current[seg] || {}), [field]: value };
        } else {
          current[seg] = { ...(current[seg] || {}) };
          current = current[seg];
        }
      });
      return updated;
    });
  };

  const handleChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [field]: value,
      },
    }));
  };

  const outcomeRow = { name: '', measure: '' };

  const handleOutcomeChange = (kind, index, field, value) => {
    setFormData((prev) => {
      const arr = [...(prev.section_5_outcomes[kind] || [])];
      if (!arr[index]) arr[index] = { ...outcomeRow };
      arr[index] = { ...arr[index], [field]: value };
      return {
        ...prev,
        section_5_outcomes: {
          ...prev.section_5_outcomes,
          [kind]: arr,
        },
      };
    });
  };

  const addOutcome = (kind) => {
    setFormData((prev) => ({
      ...prev,
      section_5_outcomes: {
        ...prev.section_5_outcomes,
        [kind]: [...(prev.section_5_outcomes[kind] || []), { ...outcomeRow }],
      },
    }));
  };

  const removeOutcome = (kind, index) => {
    setFormData((prev) => {
      const arr = [...(prev.section_5_outcomes[kind] || [])];
      arr.splice(index, 1);
      if (arr.length === 0) arr.push({ ...outcomeRow });
      return {
        ...prev,
        section_5_outcomes: {
          ...prev.section_5_outcomes,
          [kind]: arr,
        },
      };
    });
  };

  const normalizeForSave = (data) => {
    const clone = JSON.parse(JSON.stringify(data));

    const toInt = (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const n = Number(val);
      return Number.isNaN(n) ? undefined : n;
    };

    clone.section_3_study_design.planned_sample_size = toInt(
      clone.section_3_study_design.planned_sample_size,
    );
    clone.section_3_study_design.eligibility.age_min = toInt(
      clone.section_3_study_design.eligibility.age_min,
    );
    clone.section_3_study_design.eligibility.age_max = toInt(
      clone.section_3_study_design.eligibility.age_max,
    );

    const crb = clone.section_6_ethics_and_funding.certified_review_board;
    if (crb.approval_date) {
      const d = new Date(crb.approval_date);
      clone.section_6_ethics_and_funding.certified_review_board.approval_date =
        Number.isNaN(d.getTime()) ? undefined : d;
    } else {
      clone.section_6_ethics_and_funding.certified_review_board.approval_date = undefined;
    }

    // Clean outcomes: keep only rows with at least name or measure
    const cleanOutcomes = (arr) =>
      Array.isArray(arr)
        ? arr
            .map((o) => ({
              name: (o && o.name && String(o.name).trim()) || '',
              measure: (o && o.measure && String(o.measure).trim()) || '',
            }))
            .filter((o) => o.name || o.measure)
        : [];
    if (clone.section_5_outcomes) {
      clone.section_5_outcomes.primary_outcomes = cleanOutcomes(
        clone.section_5_outcomes.primary_outcomes
      );
      clone.section_5_outcomes.secondary_outcomes = cleanOutcomes(
        clone.section_5_outcomes.secondary_outcomes
      );
    }

    return { japan_registry: clone };
  };

  const handleSave = async () => {
    if (!projectId) {
      setSnackbar({
        open: true,
        message: 'Project ID is required',
        severity: 'error',
      });
      return;
    }
    setSaving(true);
    try {
      const payload = normalizeForSave(formData);
      await protocolRegistrationService.upsertByProject(projectId, payload);
      setSnackbar({
        open: true,
        message: 'Japan registry saved successfully',
        severity: 'success',
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving Japan registry:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Failed to save Japan registry',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const {
    system_metadata,
    section_1_research_identification,
    section_2_sponsor_and_personnel,
    section_3_study_design,
    section_4_intervention_and_products,
    section_5_outcomes,
    section_6_ethics_and_funding,
    section_7_transparency,
  } = formData;

  return (
    <Box sx={{ py: 3, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Japan Trial Registry (jRCT)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Capture the Japan registry (jRCT) fields aligned with the protocol and Core Datasheet.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !projectId}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>

      {/* System metadata */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          System Metadata
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Research type"
              value={system_metadata.research_type}
              onChange={(e) =>
                handleChange('system_metadata', 'research_type', e.target.value)
              }
            >
              {researchTypeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Application status"
              value={system_metadata.application_status}
              onChange={(e) =>
                handleChange('system_metadata', 'application_status', e.target.value)
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* Section 1 – Research identification */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Section 1 – Research identification
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Official title"
              value={section_1_research_identification.official_title}
              onChange={(e) =>
                handleChange('section_1_research_identification', 'official_title', e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Scientific title"
              value={section_1_research_identification.scientific_title}
              onChange={(e) =>
                handleChange(
                  'section_1_research_identification',
                  'scientific_title',
                  e.target.value,
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Scientific title acronym"
              value={section_1_research_identification.scientific_title_acronym}
              onChange={(e) =>
                handleChange(
                  'section_1_research_identification',
                  'scientific_title_acronym',
                  e.target.value,
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Public (lay) title"
              value={section_1_research_identification.public_title}
              onChange={(e) =>
                handleChange(
                  'section_1_research_identification',
                  'public_title',
                  e.target.value,
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Public title acronym"
              value={section_1_research_identification.public_title_acronym}
              onChange={(e) =>
                handleChange(
                  'section_1_research_identification',
                  'public_title_acronym',
                  e.target.value,
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="jRTC number"
              value={section_1_research_identification.jrtc_number}
              onChange={(e) =>
                handleChange('section_1_research_identification', 'jrtc_number', e.target.value)
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* Section 2 – Sponsor and personnel */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Section 2 – Sponsor and personnel
        </Typography>
        <Grid container spacing={2}>
          {/* Sponsor */}
          <Grid item xs={12}>
            <Typography variant="subtitle2">Sponsor</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Sponsor name"
              value={section_2_sponsor_and_personnel.sponsor.name}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'sponsor', {
                  ...section_2_sponsor_and_personnel.sponsor,
                  name: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Representative (for corporations)"
              value={section_2_sponsor_and_personnel.sponsor.representative}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'sponsor', {
                  ...section_2_sponsor_and_personnel.sponsor,
                  representative: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="ERAD ID (individuals)"
              value={section_2_sponsor_and_personnel.sponsor.erad_id}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'sponsor', {
                  ...section_2_sponsor_and_personnel.sponsor,
                  erad_id: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              label="Address"
              value={section_2_sponsor_and_personnel.sponsor.address}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'sponsor', {
                  ...section_2_sponsor_and_personnel.sponsor,
                  address: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Phone"
              value={section_2_sponsor_and_personnel.sponsor.phone}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'sponsor', {
                  ...section_2_sponsor_and_personnel.sponsor,
                  phone: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              type="email"
              label="Email"
              value={section_2_sponsor_and_personnel.sponsor.email}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'sponsor', {
                  ...section_2_sponsor_and_personnel.sponsor,
                  email: e.target.value,
                })
              }
            />
          </Grid>

          {/* Public query contact */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2">Public query contact</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Name"
              value={section_2_sponsor_and_personnel.public_query_contact.name}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'public_query_contact', {
                  ...section_2_sponsor_and_personnel.public_query_contact,
                  name: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Affiliation"
              value={section_2_sponsor_and_personnel.public_query_contact.affiliation}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'public_query_contact', {
                  ...section_2_sponsor_and_personnel.public_query_contact,
                  affiliation: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Phone"
              value={section_2_sponsor_and_personnel.public_query_contact.phone}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'public_query_contact', {
                  ...section_2_sponsor_and_personnel.public_query_contact,
                  phone: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              type="email"
              label="Email"
              value={section_2_sponsor_and_personnel.public_query_contact.email}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'public_query_contact', {
                  ...section_2_sponsor_and_personnel.public_query_contact,
                  email: e.target.value,
                })
              }
            />
          </Grid>

          {/* Principal investigator */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2">Principal investigator</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Name"
              value={section_2_sponsor_and_personnel.principal_investigator.name}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'principal_investigator', {
                  ...section_2_sponsor_and_personnel.principal_investigator,
                  name: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="ERAD ID"
              value={section_2_sponsor_and_personnel.principal_investigator.erad_id}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'principal_investigator', {
                  ...section_2_sponsor_and_personnel.principal_investigator,
                  erad_id: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Affiliation institution"
              value={section_2_sponsor_and_personnel.principal_investigator.affiliation_institution}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'principal_investigator', {
                  ...section_2_sponsor_and_personnel.principal_investigator,
                  affiliation_institution: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Administrator of medical institution"
              value={section_2_sponsor_and_personnel.principal_investigator.administrator_name}
              onChange={(e) =>
                handleNestedChange('section_2_sponsor_and_personnel', 'principal_investigator', {
                  ...section_2_sponsor_and_personnel.principal_investigator,
                  administrator_name: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={
                    section_2_sponsor_and_personnel.principal_investigator.admin_permission_granted
                  }
                  onChange={(e) =>
                    handleNestedChange('section_2_sponsor_and_personnel', 'principal_investigator', {
                      ...section_2_sponsor_and_personnel.principal_investigator,
                      admin_permission_granted: e.target.checked,
                    })
                  }
                />
              )}
              label="Administrator permission granted"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Section 3 – Study design */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Section 3 – Study design
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Purpose"
              value={section_3_study_design.purpose}
              onChange={(e) => handleChange('section_3_study_design', 'purpose', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Phase"
              value={section_3_study_design.phase}
              onChange={(e) => handleChange('section_3_study_design', 'phase', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Study type"
              value={section_3_study_design.study_type}
              onChange={(e) => handleChange('section_3_study_design', 'study_type', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Study period (start–end)"
              value={section_3_study_design.study_period}
              onChange={(e) =>
                handleChange('section_3_study_design', 'study_period', e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Planned sample size"
              value={section_3_study_design.planned_sample_size}
              onChange={(e) =>
                handleChange(
                  'section_3_study_design',
                  'planned_sample_size',
                  e.target.value,
                )
              }
            />
          </Grid>

          {/* Design features */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2">Design features</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={section_3_study_design.design_features.placebo_used}
                  onChange={(e) =>
                    handleNestedChange('section_3_study_design', 'design_features', {
                      ...section_3_study_design.design_features,
                      placebo_used: e.target.checked,
                    })
                  }
                />
              )}
              label="Placebo used"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={section_3_study_design.design_features.blinding_used}
                  onChange={(e) =>
                    handleNestedChange('section_3_study_design', 'design_features', {
                      ...section_3_study_design.design_features,
                      blinding_used: e.target.checked,
                    })
                  }
                />
              )}
              label="Blinding used"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={section_3_study_design.design_features.randomization_used}
                  onChange={(e) =>
                    handleNestedChange('section_3_study_design', 'design_features', {
                      ...section_3_study_design.design_features,
                      randomization_used: e.target.checked,
                    })
                  }
                />
              )}
              label="Randomization used"
            />
          </Grid>

          {/* Eligibility */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2">Eligibility</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Inclusion criteria"
              value={section_3_study_design.eligibility.inclusion_criteria}
              onChange={(e) =>
                handleNestedChange('section_3_study_design', 'eligibility', {
                  ...section_3_study_design.eligibility,
                  inclusion_criteria: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Exclusion criteria"
              value={section_3_study_design.eligibility.exclusion_criteria}
              onChange={(e) =>
                handleNestedChange('section_3_study_design', 'eligibility', {
                  ...section_3_study_design.eligibility,
                  exclusion_criteria: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Age min"
              value={section_3_study_design.eligibility.age_min}
              onChange={(e) =>
                handleNestedChange('section_3_study_design', 'eligibility', {
                  ...section_3_study_design.eligibility,
                  age_min: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Age max"
              value={section_3_study_design.eligibility.age_max}
              onChange={(e) =>
                handleNestedChange('section_3_study_design', 'eligibility', {
                  ...section_3_study_design.eligibility,
                  age_max: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Gender"
              value={section_3_study_design.eligibility.gender}
              onChange={(e) =>
                handleNestedChange('section_3_study_design', 'eligibility', {
                  ...section_3_study_design.eligibility,
                  gender: e.target.value,
                })
              }
            >
              {genderOptions.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Box>

      {/* Section 4 – Intervention and products */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Section 4 – Intervention and products
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Target condition"
              value={section_4_intervention_and_products.target_condition}
              onChange={(e) =>
                handleChange('section_4_intervention_and_products', 'target_condition', e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Condition code"
              value={section_4_intervention_and_products.condition_code}
              onChange={(e) =>
                handleChange('section_4_intervention_and_products', 'condition_code', e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              label="Intervention details"
              value={section_4_intervention_and_products.intervention_details}
              onChange={(e) =>
                handleChange(
                  'section_4_intervention_and_products',
                  'intervention_details',
                  e.target.value,
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Product category"
              value={section_4_intervention_and_products.product_category}
              onChange={(e) =>
                handleChange(
                  'section_4_intervention_and_products',
                  'product_category',
                  e.target.value,
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Product status"
              value={section_4_intervention_and_products.product_status}
              onChange={(e) =>
                handleChange(
                  'section_4_intervention_and_products',
                  'product_status',
                  e.target.value,
                )
              }
            >
              {productStatusOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Generic name / development code"
              value={section_4_intervention_and_products.generic_name}
              onChange={(e) =>
                handleChange('section_4_intervention_and_products', 'generic_name', e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Brand name"
              value={section_4_intervention_and_products.brand_name}
              onChange={(e) =>
                handleChange('section_4_intervention_and_products', 'brand_name', e.target.value)
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* Section 5 – Outcomes (separate primary and secondary, each with name + measure) */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Section 5 – Outcomes
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Primary outcomes
            </Typography>
            {(section_5_outcomes.primary_outcomes || []).map((row, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                <TextField
                  size="small"
                  label="Name"
                  placeholder="Primary outcome name"
                  value={row.name || ''}
                  onChange={(e) => handleOutcomeChange('primary_outcomes', idx, 'name', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Measure"
                  placeholder="Measure"
                  value={row.measure || ''}
                  onChange={(e) =>
                    handleOutcomeChange('primary_outcomes', idx, 'measure', e.target.value)
                  }
                  sx={{ flex: 1 }}
                />
                <Button
                  size="small"
                  color="secondary"
                  onClick={() => removeOutcome('primary_outcomes', idx)}
                  disabled={(section_5_outcomes.primary_outcomes || []).length <= 1}
                >
                  Remove
                </Button>
              </Box>
            ))}
            <Button size="small" onClick={() => addOutcome('primary_outcomes')} sx={{ mt: 0.5 }}>
              Add primary outcome
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Secondary outcomes
            </Typography>
            {(section_5_outcomes.secondary_outcomes || []).map((row, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                <TextField
                  size="small"
                  label="Name"
                  placeholder="Secondary outcome name"
                  value={row.name || ''}
                  onChange={(e) =>
                    handleOutcomeChange('secondary_outcomes', idx, 'name', e.target.value)
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Measure"
                  placeholder="Measure"
                  value={row.measure || ''}
                  onChange={(e) =>
                    handleOutcomeChange('secondary_outcomes', idx, 'measure', e.target.value)
                  }
                  sx={{ flex: 1 }}
                />
                <Button
                  size="small"
                  color="secondary"
                  onClick={() => removeOutcome('secondary_outcomes', idx)}
                  disabled={(section_5_outcomes.secondary_outcomes || []).length <= 1}
                >
                  Remove
                </Button>
              </Box>
            ))}
            <Button size="small" onClick={() => addOutcome('secondary_outcomes')} sx={{ mt: 0.5 }}>
              Add secondary outcome
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Section 6 – Ethics and funding */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Section 6 – Ethics and funding
        </Typography>
        <Grid container spacing={2}>
          {/* Certified review board */}
          <Grid item xs={12}>
            <Typography variant="subtitle2">Certified review board</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Name"
              value={section_6_ethics_and_funding.certified_review_board.name}
              onChange={(e) =>
                handleNestedChange('section_6_ethics_and_funding', 'certified_review_board', {
                  ...section_6_ethics_and_funding.certified_review_board,
                  name: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Certification number"
              value={section_6_ethics_and_funding.certified_review_board.certification_number}
              onChange={(e) =>
                handleNestedChange('section_6_ethics_and_funding', 'certified_review_board', {
                  ...section_6_ethics_and_funding.certified_review_board,
                  certification_number: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Approval date"
              InputLabelProps={{ shrink: true }}
              value={section_6_ethics_and_funding.certified_review_board.approval_date || ''}
              onChange={(e) =>
                handleNestedChange('section_6_ethics_and_funding', 'certified_review_board', {
                  ...section_6_ethics_and_funding.certified_review_board,
                  approval_date: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              label="Review result"
              value={section_6_ethics_and_funding.certified_review_board.review_result}
              onChange={(e) =>
                handleNestedChange('section_6_ethics_and_funding', 'certified_review_board', {
                  ...section_6_ethics_and_funding.certified_review_board,
                  review_result: e.target.value,
                })
              }
            />
          </Grid>

          {/* Funding source */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2">Funding source</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Provider name"
              value={section_6_ethics_and_funding.funding_source.provider_name}
              onChange={(e) =>
                handleNestedChange('section_6_ethics_and_funding', 'funding_source', {
                  ...section_6_ethics_and_funding.funding_source,
                  provider_name: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={section_6_ethics_and_funding.funding_source.research_funds_provided}
                  onChange={(e) =>
                    handleNestedChange('section_6_ethics_and_funding', 'funding_source', {
                      ...section_6_ethics_and_funding.funding_source,
                      research_funds_provided: e.target.checked,
                    })
                  }
                />
              )}
              label="Research funds provided"
            />
          </Grid>

          {/* Insurance */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2">Insurance</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={section_6_ethics_and_funding.insurance.compensation_available}
                  onChange={(e) =>
                    handleNestedChange('section_6_ethics_and_funding', 'insurance', {
                      ...section_6_ethics_and_funding.insurance,
                      compensation_available: e.target.checked,
                    })
                  }
                />
              )}
              label="Compensation available"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Liability insurance details"
              value={section_6_ethics_and_funding.insurance.liability_insurance}
              onChange={(e) =>
                handleNestedChange('section_6_ethics_and_funding', 'insurance', {
                  ...section_6_ethics_and_funding.insurance,
                  liability_insurance: e.target.value,
                })
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* Section 7 – Transparency */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Section 7 – Transparency
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={section_7_transparency.ipd_sharing_plan}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      section_7_transparency: {
                        ...prev.section_7_transparency,
                        ipd_sharing_plan: e.target.checked,
                      },
                    }))
                  }
                />
              )}
              label="IPD sharing plan (de-identified data)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="IPD sharing details"
              value={section_7_transparency.ipd_sharing_details}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  section_7_transparency: {
                    ...prev.section_7_transparency,
                    ipd_sharing_details: e.target.value,
                  },
                }))
              }
            />
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JapanTrialRegistry;